import { COLORS } from './constants.js';

// SVG path for zigzag line chart (enlarged version for clipping)
const ZIGZAG_LINE_PATH = `M-50 37.1905L-36.9517 24.1422C-29.1412 16.3317 -16.4779 16.3317 -8.6674 24.1422L17.4293 50.2388C25.2398 58.0493 37.9031 58.0493 45.714 50.2388L71.81 24.1422C79.621 16.3317 92.284 16.3317 100.095 24.1422L126.191 50.2388C134.002 58.0493 146.665 58.0493 154.475 50.2388L180.572 24.1422C188.383 16.3317 201.046 16.3317 208.856 24.1422L234.953 50.2388C242.764 58.0493 255.427 58.0493 263.237 50.2388L289.334 24.1422C297.145 16.3317 309.808 16.3317 317.618 24.1422L343.715 50.2388C351.525 58.0493 364.189 58.0493 371.999 50.2388L398.096 24.1422C405.906 16.3317 418.57 16.3317 426.38 24.1422L452.477 50.2388C460.287 58.0493 472.951 58.0493 480.761 50.2388L506.858 24.1422C514.668 16.3317 527.332 16.3317 535.142 24.1422L561.239 50.2388C569.049 58.0493 581.713 58.0493 589.523 50.2388L641 10`;

// Create skeleton HTML structure
export function createSkeletonHTML() {
  return `
    <div class="ein-skeleton-loader" id="ein-skeleton-loader">
      <div class="ein-skeleton-chart">
        <!-- Y-axis -->
        <div class="ein-skeleton-y-axis">
          <div class="ein-skeleton-y-labels">
            <div class="ein-skeleton-label-rect"></div>
            <div class="ein-skeleton-label-rect"></div>
            <div class="ein-skeleton-label-rect"></div>
            <div class="ein-skeleton-label-rect"></div>
            <div class="ein-skeleton-label-rect"></div>
            <div class="ein-skeleton-label-rect"></div>
          </div>
          <div class="ein-skeleton-y-line"></div>
        </div>

        <!-- Chart area -->
        <div class="ein-skeleton-chart-area">
          <div class="ein-skeleton-line-container">
            <svg class="ein-skeleton-line-chart" viewBox="0 0 620 100" fill="none">
              <g opacity="0.5">
                <path d="${ZIGZAG_LINE_PATH}" stroke="${COLORS.axisStroke}" stroke-width="26"/>
                <path d="${ZIGZAG_LINE_PATH}" stroke="${COLORS.skeletonStroke}"/>
              </g>
            </svg>
          </div>
        </div>

        <!-- X-axis -->
        <div class="ein-skeleton-x-axis">
          <div class="ein-skeleton-x-line"></div>
          <div class="ein-skeleton-x-labels">
            <div class="ein-skeleton-label-rect"></div>
            <div class="ein-skeleton-label-rect"></div>
            <div class="ein-skeleton-label-rect"></div>
          </div>
        </div>
      </div>
    </div>
  `;
}

const LABEL_HEIGHT = 12;
const Y_AXIS_HEIGHT = 170;

export const SKELETON_STYLES = `
/* Main container */
.ein-skeleton-loader {
  position: relative;
  top: 10px;
  width: 100%;
  height: 195px;
  background: ${COLORS.white};
}
.ein-skeleton-loader.hidden {
  display: none;
}
.ein-skeleton-loader.fade-out {
  opacity: 0;
  transition: opacity 200ms ease-out;
}

/* Chart layout */
.ein-skeleton-chart {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Y-axis components */
.ein-skeleton-y-axis {
  position: absolute;
  left: 0;
  top: 0;
  width: 33px;
  height: ${Y_AXIS_HEIGHT}px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
}
.ein-skeleton-y-labels {
  position: absolute;
  right: 8px;
  top: 0;
  height: ${Y_AXIS_HEIGHT}px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
}
.ein-skeleton-y-line {
  position: absolute;
  right: 0;
  top: 0;
  width: 1px;
  height: 195px;
  background-color: ${COLORS.axisStroke};
}

/* X-axis components */
.ein-skeleton-x-axis {
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  height: 20px;
}
.ein-skeleton-x-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 5px;
  height: 1px;
  background-color: ${COLORS.axisStroke};
}
.ein-skeleton-x-labels {
  position: absolute;
  top: 4px;
  left: 38px;
  right: 8px;
  height: ${LABEL_HEIGHT}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Chart area */
.ein-skeleton-chart-area {
  position: absolute;
  left: 33px;
  top: 0;
  right: 5px;
  height: 175px;
  overflow: hidden;
}
/* Line chart container for clipping */
.ein-skeleton-line-container {
  position: absolute;
  left: 0;
  top: 37px;
  width: 100%;
  height: 100px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}
.ein-skeleton-line-chart {
  width: 100%;
  height: 100px;
  animation: ein-skeleton-fade 0.5s ease-in-out infinite alternate;
}

/* Label rectangles */
.ein-skeleton-label-rect {
  background-color: ${COLORS.axisStroke};
  border-radius: 2px;
}
.ein-skeleton-y-labels .ein-skeleton-label-rect:first-child {
  width: 24px; /* "Days" label */
  height: ${LABEL_HEIGHT}px;
}
.ein-skeleton-y-labels .ein-skeleton-label-rect:not(:first-child) {
  width: 15px; /* Number labels */
  height: ${LABEL_HEIGHT}px;
}
.ein-skeleton-x-labels .ein-skeleton-label-rect {
  width: 20px; /* Month labels */
  height: ${LABEL_HEIGHT}px;
}

/* Animation */
@keyframes ein-skeleton-fade {
  0% { opacity: 0.5; }
  100% { opacity: 0.8; }
}
`;
